import statesInverted from '../constants/statesInverted.json';
import states from '../constants/states.json';

const statesNameToCode = Object.entries(states).reduce((acc, [code, name]) => {
    acc[name.toLowerCase()] = code;
    return acc;
}, {});

export const CITY_FALLBACK_TYPES = [
    'locality',
    'postal_town',
    'sublocality',
    'sublocality_level_1',
    'neighborhood',
    'administrative_area_level_3',
];

export const ACCEPTED_AUTOCOMPLETE_LOCATION_TYPES = ['ROOFTOP', 'RANGE_INTERPOLATED'];

export const normalizeZip = (zip) => {
    if (!zip) return '';
    const value = String(zip);
    const match = value.match(/^(\d{5})/);
    return match ? match[1] : value;
};

export const toShortState = (state) => {
    if (!state) return '';
    const trimmed = String(state).trim();
    if (!trimmed) return '';

    const upper = trimmed.toUpperCase();
    return (
        statesInverted[state] ||
        statesInverted[trimmed] ||
        (states[upper] ? upper : statesNameToCode[trimmed.toLowerCase()] || state)
    );
};

// code -> full name; idempotent, display-only
export const toFullState = (state) => {
    if (!state) return '';
    const trimmed = String(state).trim();
    if (!trimmed) return '';

    return states[trimmed.toUpperCase()] || state;
};

export const buildCanonicalAddress = ({ street, city, state, zip }) =>
    [street, city, toShortState(state), normalizeZip(zip)].filter((part) => part).join(', ');

export const buildCanonicalFullAddress = ({ street, unit, city, state, zip }) =>
    [street, unit, city, toShortState(state), normalizeZip(zip)].filter((part) => part).join(', ');

export const findFirstComponent = (components = [], types = []) => {
    for (const type of types) {
        const component = components.find((comp) => comp.types.includes(type));
        if (component) return component;
    }
    return null;
};

export function extractAddressComponents(components = []) {
    const streetNum = findFirstComponent(components, ['street_number']);
    const route = findFirstComponent(components, ['route']);
    const city = findFirstComponent(components, CITY_FALLBACK_TYPES);
    const state = findFirstComponent(components, ['administrative_area_level_1']);
    const zip = findFirstComponent(components, ['postal_code']);

    let street = '';
    if (streetNum?.long_name) street += streetNum.long_name;
    if (route?.long_name) street += (street ? ' ' : '') + route.long_name;

    return {
        street,
        city: city?.long_name || '',
        state: state?.short_name || '',
        zip: normalizeZip(zip?.long_name),
        hasStreetNumber: !!streetNum,
        hasRoute: !!route,
    };
}

export const streetFromSuggestion = (suggestion, description) => {
    const mainText = suggestion?.formattedSuggestion?.mainText;
    if (mainText && mainText.trim()) return mainText.trim();

    const desc = String(description ?? '').trim();
    if (!desc) return '';

    const firstSegment = desc.split(',')[0].trim();
    return firstSegment || desc;
};

export const isResolvedAutocompleteAddress = (split) => !!(split?.resolved && split?.street);

export const autocompleteAddressLine = (value, split) => split?.addressLine || split?.street || value || '';

export const autocompleteCanonicalAddress = (value, split) => {
    const structuredAddress = buildCanonicalAddress({
        street: split?.street || split?.addressLine,
        city: split?.city,
        state: split?.state,
        zip: split?.zip,
    });
    const hasStructuredContext = !!(split?.city || split?.state || split?.zip);

    return (
        (hasStructuredContext ? structuredAddress : '') ||
        split?.address ||
        split?.fullAddress ||
        autocompleteAddressLine(value, split)
    );
};

export const autocompleteFullAddress = (value, split) =>
    split?.fullAddress || autocompleteCanonicalAddress(value, split);

export const autocompleteLatLngPatch = (split, latKey, lngKey) =>
    isResolvedAutocompleteAddress(split) && split?.lat != null && split?.lng != null
        ? { [latKey]: split.lat, [lngKey]: split.lng }
        : {};

export const clearUnresolvedAutocompleteLatLngPatch = (split, latKey, lngKey) =>
    !isResolvedAutocompleteAddress(split) && latKey && lngKey ? { [latKey]: null, [lngKey]: null } : {};

export function buildAutocompleteAddressUpdate({
    extracted,
    state,
    fallbackStreet,
    address,
    formattedAddress,
    lat,
    lng,
    partialMatch = false,
    locationType = '',
}) {
    let street;
    if (extracted.hasStreetNumber) {
        street = extracted.street;
    } else if (extracted.hasRoute) {
        street = fallbackStreet;
    } else {
        street = state?.street || '';
    }

    const city = extracted.city || state?.city || '';
    const stateValue = extracted.state || state?.state || '';
    const zip = extracted.zip || state?.zip || '';
    const canonicalAddress = buildCanonicalAddress({ street, city, state: stateValue, zip });

    const hasStreetLevelMatch = extracted.hasStreetNumber && extracted.hasRoute;
    const hasAcceptedLocationType = ACCEPTED_AUTOCOMPLETE_LOCATION_TYPES.includes(locationType);
    const resolved = hasStreetLevelMatch && !partialMatch && hasAcceptedLocationType;

    return {
        street,
        city,
        state: stateValue,
        zip,
        lat,
        lng,
        partialMatch,
        locationType,
        addressLine: street,
        address: canonicalAddress,
        fullAddress: canonicalAddress || formattedAddress || address || '',
        resolved,
    };
}

export function mergeGeocodeFields({ order, geocodeResults, geocodeFailed, isPickup, swap = false }) {
    const hasResult = !!(geocodeResults && Object.keys(geocodeResults).length > 0);
    if (!hasResult) return {};

    const hasGeocodedRoad = !!geocodeResults.street;

    if (isPickup) {
        const street = geocodeResults.street || order.pickup_street_address || '';
        const city = geocodeResults.city || order.pickup_city || '';
        const state = geocodeResults.state || order.pickup_state || '';
        const zip = geocodeResults.zip || order.pickup_zip || '';

        return {
            pickup_full_address: [street, order.pickup_unit, city, state, zip]
                .filter((val) => val && val.length > 0)
                .join(', '),
            pickup_address: buildCanonicalAddress({ street, city, state, zip }) || order.pickup_address || '',
            pickup_city: city,
            pickup_state: state,
            pickup_street_address: street,
            pickup_zip: zip,
            pickup_lat: hasGeocodedRoad ? geocodeResults.lat : order.pickup_lat,
            pickup_lng: hasGeocodedRoad ? geocodeResults.lng : order.pickup_lng,
            pickup_address_geocode_failed: geocodeFailed,

            ...(swap
                ? {
                      dropoff_full_address: order.pickup_full_address,
                      dropoff_address: order.pickup_address,
                      dropoff_city: order.pickup_city,
                      dropoff_state: order.pickup_state,
                      dropoff_street: order.pickup_street_address,
                      dropoff_zip: order.pickup_zip,
                      dropoff_lat: order.pickup_lat,
                      dropoff_lng: order.pickup_lng,
                  }
                : {}),
        };
    }

    const street = geocodeResults.street || order.dropoff_street || '';
    const city = geocodeResults.city || order.dropoff_city || '';
    const state = geocodeResults.state || order.dropoff_state || '';
    const zip = geocodeResults.zip || order.dropoff_zip || '';

    return {
        dropoff_full_address: [street, order.dropoff_unit, city, state, zip]
            .filter((val) => val && val.length > 0)
            .join(', '),
        dropoff_address: buildCanonicalAddress({ street, city, state, zip }) || order.dropoff_address || '',
        dropoff_city: city,
        dropoff_state: state,
        dropoff_street: street,
        dropoff_zip: zip,
        dropoff_lat: hasGeocodedRoad ? geocodeResults.lat : order.dropoff_lat,
        dropoff_lng: hasGeocodedRoad ? geocodeResults.lng : order.dropoff_lng,
        dropoff_address_geocode_failed: geocodeFailed,
    };
}
